import { useMemo, useState, useCallback, useEffect } from 'react'

import SaveAltIcon from '@mui/icons-material/SaveAlt'
import { TextField } from '@mui/material'
import Box from '@mui/material/Box'
import Button, { ButtonProps } from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import {
  useGridApiContext,
  type GridColDef,
  type GridToolbarExportProps,
  GridToolbarContainer,
  GridToolbar,
} from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import DatePicker from 'components/DatePicker'
import LoadingFullPage from 'components/LoadingFullPage'
import SelectWithCheckbox, {
  SelectWithCheckboxOptions,
} from 'components/SelectWithCheckbox'
import Table from 'components/Table'
import { QUERY_KEYS } from 'constants/keys'
import { INPUT_MASK } from 'constants/masks'
import {
  ACTIVITY_STATUS,
  ACTIVITY_STATUS_OPTIONS,
  BUDGET_STATUS_OPTIONS,
} from 'constants/types'
import { useGlobalState } from 'contexts/global-state'
import { useSearchParams } from 'react-router-dom'
import { ApiService } from 'services/api'
import { formatUtcToBrasilia } from 'utils/formatDate'
type GenerateResultsParams = {
  queryActivityStatus?: { value: string }[] | null
  queryStartDate?: string | null
  queryEndDate?: string | null
  clearFilters?: boolean
}

export default function RevenueCostReport() {
  const [generateResult, setGenerateResult] = useState<SalesReportResponse[]>(
    [],
  )
  const [queryStartDateData, setQueryStartDateData] =
    useState<GenerateResultsParams['queryStartDate']>('')
  const [queryEndDateData, setQueryEndDateData] =
    useState<GenerateResultsParams['queryEndDate']>('')
  const [queryActivityStatusData, setQueryActivityStatusData] =
    useState<GenerateResultsParams['queryActivityStatus']>(null)

  const [selectedInstitutions, setSelectedInstitutions] =
    useState<SelectWithCheckboxOptions>([])

  const [selectedBudgets, setSelectedBudgets] =
    useState<SelectWithCheckboxOptions>([])

  const [selectedActivityStatus, setSelectedActivityStatus] =
    useState<SelectWithCheckboxOptions>([])

  const [selectedBudgetStatus, setSelectedBudgetStatus] =
    useState<SelectWithCheckboxOptions>([])

  const [activityCode, setActivityCode] = useState<string>('')

  const [startDate, setStartDate] = useState<string | Date | null | undefined>(
    null,
  )

  const [endDate, setEndDate] = useState<string | Date | null | undefined>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { openErrorToast } = useGlobalState()

  const tableColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: '#',
        headerName: '#',
        filterable: false,
        width: 70,
        renderCell: (cell) => cell.api.getRowIndex(cell.row.id) + 1,
      },
      {
        field: 'name',
        headerName: 'Orçamento',
        flex: 1,
      },
      {
        field: 'institution.fantasy_name',
        headerName: 'Instituição',
        flex: 1,
        valueGetter: (params) => params.row.institution.fantasy_name,
      },
      {
        field: 'total_without_margin',
        headerName: 'Valor sem margem',
        width: 200,
        valueGetter: (params) => params.value,
      },
      {
        field: 'total_with_margin',
        headerName: 'Valor com margem',
        width: 200,
        valueGetter: (params) => params.value,
      },
      {
        field: 'value_per_person',
        headerName: 'Valor por pessoa',
        width: 200,
        valueGetter: (params) => params.value,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 200,
        valueGetter: (params) => {
          if (params.value === 'awaiting_approval') {
            return 'Aguardando aprovação'
          }
          if (params.value === 'approved') {
            return 'Aprovado'
          }
          if (params.value === 'reproved') {
            return 'Reprovado'
          }
          return ''
        },
      },
      {
        field: 'created_at',
        headerName: 'Data',
        width: 200,
        valueGetter: (params) =>
          formatUtcToBrasilia(params.value as string, INPUT_MASK.DATE_LOCALE),
      },
    ],
    [],
  )

  const { data: institutions } = useQuery<GelAllNamesAndIdsOptions[]>(
    QUERY_KEYS.INSTITUTIONS.NAMES_AND_IDS,
    () =>
      ApiService.Institutions.getAllNamesAndIds({
        fields: ['id', 'fantasy_name', 'document', 'type'],
        relations: [],
      }),
  )

  const { data: budgets } = useQuery<SelectWithCheckboxOptions>(
    QUERY_KEYS.BUDGETS.LIST,
    () => ApiService.Budgets.getAllNamesAndIds(),
  )

  const [queryParas] = useSearchParams()

  const generateReport = useCallback(
    ({
      queryStartDate,
      queryEndDate,
      queryActivityStatus,
      clearFilters,
    }: GenerateResultsParams) => {
      setQueryStartDateData(queryStartDate)
      setQueryEndDateData(queryEndDate)
      setQueryActivityStatusData(queryActivityStatus)
      setIsLoading(true)
      let filters = {
        institutions: selectedInstitutions.map((si) => Number(si.value)),
        status: selectedBudgetStatus.map((s) => String(s.value)),
        activityStatus: (queryActivityStatus || selectedActivityStatus).map(
          (s) => String(s.value),
        ),
        budgets: selectedBudgets.map((c) => Number(c.value)),
        activityCode,
        start: queryStartDate
          ? queryStartDate
          : startDate
          ? String(startDate).split('/').reverse().join('-')
          : undefined,
        end: queryEndDate
          ? queryEndDate
          : endDate
          ? String(endDate).split('/').reverse().join('-')
          : undefined,
      }
      if (clearFilters) {
        filters = {
          institutions: [],
          status: [],
          activityStatus: [],
          budgets: [],
          activityCode: '',
          start: '',
          end: '',
        }
      }
      ApiService.Reports.generateRevenueCostBudget(filters)
        .then((response) => {
          setGenerateResult(response)
          if (!response.length) {
            openErrorToast({ message: 'Nenhum resultado encontrado.' })
          }
          setIsLoading(false)
        })
        .catch(() => {
          setIsLoading(false)
        })
    },
    [
      selectedInstitutions,
      selectedBudgetStatus,
      selectedActivityStatus,
      selectedBudgets,
      activityCode,
      startDate,
      endDate,
      openErrorToast,
    ],
  )

  useEffect(() => {
    const queryStartDate = queryParas.get('start_date')
    const queryEndDate = queryParas.get('end_date')
    if (queryStartDate) {
      setStartDate(queryStartDate)
    }
    if (queryEndDate) {
      setEndDate(queryEndDate)
    }
    const queryActivityStatus = queryParas.get('activityStatus') as
      | ActivityStatus
      | undefined
    const activityStatus = []
    if (queryActivityStatus) {
      const situation = {
        value: queryActivityStatus,
        label: ACTIVITY_STATUS[queryActivityStatus],
      }
      setSelectedActivityStatus([situation])
      activityStatus.push(situation)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParas])

  function CustomToolbar(props: GridToolbarExportProps) {
    const { user, openErrorToast } = useGlobalState()
    const apiRef = useGridApiContext()

    const generateExportCsv = useCallback(
      () =>
        apiRef.current.exportDataAsCsv({
          ...props.csvOptions,
          utf8WithBom: true,
          delimiter: ';',
        }),
      [apiRef, props.csvOptions],
    )

    const handleExport = useCallback(async () => {
      try {
        await ApiService.AuditLogs.create({
          entity: 'Report',
          type: 'export',
          changes: JSON.stringify([
            {
              valueAfter: {
                institutions: selectedInstitutions.map((si) =>
                  Number(si.value),
                ),
                status: ['finished', 'payment_confirmed'],
                activityStatus: (
                  queryActivityStatusData || selectedActivityStatus
                ).map((s) => String(s.value)),

                budgets: selectedBudgets.map((c) => Number(c.value)),
                activityCode,

                start: queryStartDateData
                  ? queryStartDateData
                  : startDate
                  ? String(startDate).split('/').reverse().join('-')
                  : undefined,
                end: queryEndDateData
                  ? queryEndDateData
                  : endDate
                  ? String(endDate).split('/').reverse().join('-')
                  : undefined,
              },
            },
          ]),
          client_id: null,
          entity_id: 0,
          user_id: user?.id,
        })
        generateExportCsv()
      } catch (error: unknown) {
        openErrorToast({
          message: `Ocorreu um erro ao criar um registro na auditoria: ${
            (error as ApiError).message
          }`,
        })
      }
    }, [generateExportCsv, openErrorToast, user?.id])

    const buttonBaseProps: ButtonProps = {
      color: 'primary',
      size: 'small',
      startIcon: <SaveAltIcon />,
    }
    return (
      <GridToolbarContainer>
        <GridToolbar
          csvOptions={{ disableToolbarButton: true }}
          printOptions={{ disableToolbarButton: true }}
        />
        <Button {...buttonBaseProps} onClick={() => handleExport()}>
          Exportar
        </Button>
      </GridToolbarContainer>
    )
  }

  if (!institutions?.length || !budgets?.length || isLoading) {
    return <LoadingFullPage />
  }

  return (
    <Box component="main" sx={{ flexGrow: 1, py: 8 }}>
      <Container maxWidth={false}>
        <Box>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ m: 1 }} variant="h4">
              Relatório de Receita/Custo
            </Typography>
          </Box>

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'flex-start',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <SelectWithCheckbox
              options={budgets}
              value={selectedBudgets}
              onSelect={setSelectedBudgets}
              selectAllLabel="Todas os orçamentos"
              placeholder="Orçamentos"
            />

            <SelectWithCheckbox
              options={institutions}
              value={selectedInstitutions}
              onSelect={setSelectedInstitutions}
              selectAllLabel="Todas as instituições"
              placeholder="Instituições"
            />

            <TextField
              label="Código da atividade"
              type="text"
              variant="outlined"
              value={activityCode}
              onChange={(event) => {
                setActivityCode(event.target.value)
              }}
            />

            <SelectWithCheckbox
              options={BUDGET_STATUS_OPTIONS}
              value={selectedBudgetStatus}
              onSelect={setSelectedBudgetStatus}
              selectAllLabel="Todos os status"
              placeholder="Status do orçamento"
            />

            <SelectWithCheckbox
              options={ACTIVITY_STATUS_OPTIONS.filter(
                (i) => i.value !== 'cancelled',
              )}
              value={selectedActivityStatus}
              onSelect={setSelectedActivityStatus}
              selectAllLabel="Todas as situações"
              placeholder="Status da atividade"
            />

            <DatePicker
              label="Data inicial"
              id="startDate"
              value={startDate}
              onChange={(newValue) => {
                setStartDate(newValue)
              }}
            />

            <DatePicker
              label="Data final"
              id="endDate"
              value={endDate}
              onChange={(newValue) => {
                setEndDate(newValue)
              }}
            />

            <Button
              color="primary"
              variant="contained"
              onClick={() => generateReport({})}
              disabled={
                !selectedInstitutions.length &&
                !selectedActivityStatus.length &&
                !selectedBudgets.length &&
                !selectedBudgetStatus.length &&
                !startDate &&
                !endDate &&
                !activityCode
              }
            >
              Gerar relatório
            </Button>

            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                setSelectedInstitutions([])
                setSelectedBudgets([])
                setSelectedActivityStatus([])
                setSelectedBudgetStatus([])
                setStartDate(null)
                setEndDate(null)
                setActivityCode('')
                generateReport({ clearFilters: true })
              }}
            >
              Limpar filtros
            </Button>
          </Box>
        </Box>

        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Table
              rows={generateResult}
              columns={tableColumns}
              customToolbar={() => <CustomToolbar />}
            />
          </CardContent>
        </Card>
      </Container>
    </Box>
  )
}
